// API client for communicating with the backend
// Uses session tokens from App Bridge for authentication

import { getSessionToken } from './shopify';

const BASE_URL = import.meta.env.PROD ? 'https://api.tehmop.com' : '';  // Vite proxy handles /api -> localhost:3000 in dev

// Dev mock mode — when running outside Shopify admin (no App Bridge),
// return mock data so the UI can be developed without a live backend.
const DEV_MOCK = !import.meta.env.PROD && !window.shopify;

const MOCK_WIDGETS: Record<string, WidgetConfig> = {
  countdown: {
    widget_type: 'countdown',
    enabled: true,
    custom_css: '',
    config: { title: 'Sale ends in', placement: 'inline', timer_mode: 'fixed', end_date: '2026-03-01T00:00', end_behavior: 'hide', end_message: '', redirect_url: '', bg_color: '#111111', text_color: '#ffffff', accent_color: '#ff4500' },
  },
  sales_popup: {
    widget_type: 'sales_popup',
    enabled: false,
    custom_css: '',
    config: { frequency: 30, display_duration: 6, delay_between: 5, max_per_session: 5, position: 'bottom-left', bg_color: '#ffffff', text_color: '#333333', accent_color: '#c33600' },
  },
  stock_counter: {
    widget_type: 'stock_counter',
    enabled: false,
    custom_css: '',
    config: { threshold: 10, message_template: 'Only {count} left!', sold_out_message: 'Sold out', hide_when_zero: false, bg_color: '#fff3cd', text_color: '#856404', accent_color: '#d63384' },
  },
  announcement_bar: {
    widget_type: 'announcement_bar',
    enabled: true,
    custom_css: '',
    config: { announcements: [{ text: 'Free shipping on orders over $50!', link: '', start: '', end: '' }], rotation_interval: 5, placement: 'top', show_close: true, bg_color: '#008060', text_color: '#ffffff', accent_color: '#c9ffc9' },
  },
  visitor_counter: {
    widget_type: 'visitor_counter',
    enabled: false,
    custom_css: '',
    config: { threshold: 5, message_template: '{count} people viewed this today', bg_color: '#f3f4f6', text_color: '#374151', accent_color: '#6b7280' },
  },
};

export interface WidgetConfig {
  widget_type: string;
  config: Record<string, unknown>;
  enabled: boolean;
  custom_css: string;
}

export interface BillingStatus {
  plan: string;
  charge_id: string | null;
  features: Record<string, unknown>;
}

/**
 * Make an authenticated API request.
 * Adds the session token as a Bearer token.
 */
async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getSessionToken();
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('Content-Type', 'application/json');

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || `API error: ${response.status}`);
  }

  return response;
}

/**
 * Exchange the App Bridge session token for an offline access token.
 * Must be called once at startup before any /api/* calls.
 */
export async function bootstrapSession(): Promise<void> {
  if (DEV_MOCK) { console.log('[dev-mock] bootstrapSession — skipped'); return; }
  await apiFetch('/auth/token', { method: 'POST' });
}

export async function getWidgetConfig(widgetType: string): Promise<WidgetConfig> {
  if (DEV_MOCK) return structuredClone(MOCK_WIDGETS[widgetType] ?? { widget_type: widgetType, enabled: false, config: {}, custom_css: '' });
  const response = await apiFetch(`/api/widgets/${widgetType}`);
  return response.json() as Promise<WidgetConfig>;
}

export async function updateWidgetConfig(widgetType: string, config: Record<string, unknown>, customCss?: string): Promise<WidgetConfig> {
  if (DEV_MOCK) {
    const w = MOCK_WIDGETS[widgetType] ?? { widget_type: widgetType, enabled: false, config: {}, custom_css: '' };
    w.config = config;
    if (customCss !== undefined) w.custom_css = customCss;
    console.log('[dev-mock] updateWidgetConfig', widgetType, config);
    return structuredClone(w);
  }
  const body: Record<string, unknown> = { config };
  if (customCss !== undefined) body.custom_css = customCss;
  const response = await apiFetch(`/api/widgets/${widgetType}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return response.json() as Promise<WidgetConfig>;
}

export async function toggleWidget(widgetType: string, enabled: boolean, config: Record<string, unknown> = {}): Promise<WidgetConfig> {
  if (DEV_MOCK) {
    const w = MOCK_WIDGETS[widgetType] ?? { widget_type: widgetType, enabled: false, config: {}, custom_css: '' };
    w.enabled = enabled;
    if (Object.keys(config).length > 0) w.config = config;
    console.log('[dev-mock] toggleWidget', widgetType, enabled);
    return structuredClone(w);
  }
  const response = await apiFetch(`/api/widgets/${widgetType}`, {
    method: 'PUT',
    body: JSON.stringify({ config, enabled }),
  });
  return response.json() as Promise<WidgetConfig>;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  if (DEV_MOCK) return { plan: 'starter', charge_id: null, features: {} };
  const response = await apiFetch('/api/billing/status');
  return response.json() as Promise<BillingStatus>;
}

export async function subscribeToPlan(plan: string): Promise<{ confirmation_url: string | null }> {
  if (DEV_MOCK) { console.log('[dev-mock] subscribeToPlan', plan); return { confirmation_url: plan === 'starter' ? null : '#' }; }
  const response = await apiFetch('/api/billing/subscribe', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
  return response.json() as Promise<{ confirmation_url: string | null }>;
}

// --- Analytics ---

export interface DailyEntry {
  date: string;
  impressions: number;
  clicks: number;
}

export interface AnalyticsTotals {
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface AnalyticsResponse {
  widget_type: string;
  range_days: number;
  start_date: string;
  end_date: string;
  daily: DailyEntry[];
  totals: AnalyticsTotals;
}

export interface WidgetSummary {
  widget_type: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface SummaryResponse {
  range_days: number;
  widgets: WidgetSummary[];
}

export async function getAnalytics(widgetType: string, range?: number): Promise<AnalyticsResponse> {
  if (DEV_MOCK) {
    const days = range ?? 7;
    const daily: DailyEntry[] = Array.from({ length: days }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (days - 1 - i));
      return { date: d.toISOString().slice(0, 10), impressions: Math.floor(Math.random() * 200) + 10, clicks: Math.floor(Math.random() * 20) };
    });
    const totals = daily.reduce((t, d) => ({ impressions: t.impressions + d.impressions, clicks: t.clicks + d.clicks, ctr: 0 }), { impressions: 0, clicks: 0, ctr: 0 });
    totals.ctr = totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0;
    return { widget_type: widgetType, range_days: days, start_date: daily[0].date, end_date: daily[daily.length - 1].date, daily, totals };
  }
  const params = new URLSearchParams({ widget_type: widgetType });
  if (range) params.set('range', range.toString());
  const response = await apiFetch(`/api/analytics?${params}`);
  return response.json() as Promise<AnalyticsResponse>;
}

// --- Theme colors ---

export interface ThemeColors {
  bg_color: string | null;
  text_color: string | null;
  accent_color: string | null;
}

export async function getThemeColors(): Promise<ThemeColors> {
  if (DEV_MOCK) return { bg_color: '#1a1a2e', text_color: '#eaeaea', accent_color: '#e94560' };
  const response = await apiFetch('/api/theme-colors');
  return response.json() as Promise<ThemeColors>;
}

export async function getAnalyticsSummary(range?: number): Promise<SummaryResponse> {
  if (DEV_MOCK) {
    return {
      range_days: range ?? 7,
      widgets: ['countdown', 'sales_popup', 'stock_counter', 'announcement_bar', 'visitor_counter'].map(wt => ({
        widget_type: wt, impressions: Math.floor(Math.random() * 1000) + 100, clicks: Math.floor(Math.random() * 100), ctr: Math.random() * 10,
      })),
    };
  }
  const params = range ? `?range=${range}` : '';
  const response = await apiFetch(`/api/analytics/summary${params}`);
  return response.json() as Promise<SummaryResponse>;
}
